import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { adminApi } from '../api/admin';
import './DashboardPage.css';
import { SERIES, CHART_GRID, CHART_AXIS, SEMANTIC } from '../theme/chart';

const HINT_LABELS: Record<string, string> = {
  r1: '같은 학교', r2: '같은 직장', r3: '자주 만남', r4: '오래 못 만남',
  r5: '온라인', r6: '친구의 친구', e1: '먼저 못 걸어', e2: '편해',
  e3: '할 말 많아', e4: '자주 생각', e5: '눈이 갔어', e6: '떨려',
  t1: '1년 이내', t2: '5년 이상', t3: '대화 오래됨', t4: '다시 연락',
  o1: '별명 저장', o2: '공통 친구', o3: '빌린 거', o4: 'SNS',
};

const PHASE_LABELS: Record<string, string> = {
  hint: '힌트', message_1: '1단', message_2: '2단', message_3: '3단',
};

const COLORS = SERIES;

export default function StatsPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getStats().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">로딩 중...</div>;
  if (!data) return null;

  const hintData = (data.hintStats || []).map((h: any) => ({
    ...h,
    label: HINT_LABELS[h.hintId] || h.hintId,
  }));

  return (
    <div className="dashboard">
      <h2>통계 & 분석</h2>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-value">{data.overallMatchRate}%</div>
          <div className="kpi-label">전체 매칭률</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{data.guessConversionRate}%</div>
          <div className="kpi-label">픽기회 전환율</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{data.chainStats?.totalChains || 0}</div>
          <div className="kpi-label">총 체인 수</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{data.chainStats?.avgLength || 0}</div>
          <div className="kpi-label">평균 체인 길이</div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-section">
          <h3>매칭 추이 (30일)</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.matchTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} stroke={CHART_AXIS} tickLine={false} />
                <YAxis allowDecimals={false} fontSize={11} stroke={CHART_AXIS} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="matched" stroke={SEMANTIC.matched} name="매칭" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="failed" stroke={SEMANTIC.failed} name="실패" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-section">
          <h3>긁기율 (카드별)</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={(data.scratchRates || []).map((s: any) => ({ ...s, label: PHASE_LABELS[s.phase] || s.phase }))}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="label" fontSize={12} stroke={CHART_AXIS} tickLine={false} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} fontSize={11} stroke={CHART_AXIS} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v: any) => `${v}%`} />
                <Bar dataKey="rate" name="긁기율" radius={[4, 4, 0, 0]}>
                  {(data.scratchRates || []).map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="chart-section" style={{ marginBottom: 24 }}>
        <h3>힌트별 매칭률</h3>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={hintData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} fontSize={11} stroke={CHART_AXIS} tickLine={false} />
              <YAxis dataKey="label" type="category" fontSize={11} width={70} stroke={CHART_AXIS} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: any) => `${v}%`} />
              <Bar dataKey="rate" fill={SERIES[3]} radius={[0, 4, 4, 0]} name="매칭률" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="chart-section">
        <h3>체인 통계</h3>
        <div className="detail-grid" style={{ padding: 0, boxShadow: 'none' }}>
          <div className="detail-field">
            <div className="detail-label">총 체인 수</div>
            <div className="detail-value">{data.chainStats?.totalChains || 0}</div>
          </div>
          <div className="detail-field">
            <div className="detail-label">평균 길이</div>
            <div className="detail-value">{data.chainStats?.avgLength || 0}</div>
          </div>
          <div className="detail-field">
            <div className="detail-label">최장 체인</div>
            <div className="detail-value">{data.chainStats?.maxLength || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
